<template>
  <div class="analysis-page">
    <!-- Back nav -->
    <button class="back-btn" @click="$router.push(`/procurement/sourcing-events/${$route.params.id}`)">
      <font-awesome-icon icon="arrow-left" class="me-1" /> Back to Event
    </button>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Loading bid analysis…</p>
    </div>

    <div v-else-if="error" class="error-state">
      <font-awesome-icon icon="lock" class="error-icon" />
      <h5>{{ error }}</h5>
      <button class="btn-outline" @click="$router.back()">Go Back</button>
    </div>

    <template v-else-if="data">

      <!-- Page header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Bid Analysis</h5>
          <p class="page-sub">
            <span class="mono">{{ data.event.event_number }}</span>
            &nbsp;·&nbsp; {{ data.event.title }}
            &nbsp;·&nbsp; <span :class="['ev-status', `ev-status--${data.event.status}`]">{{ formatStatus(data.event.status) }}</span>
          </p>
        </div>
        <div class="header-actions">
          <button
            v-if="data.all_lines_awarded && data.event.status !== 'awarded'"
            class="btn-close-event"
            :disabled="closingEvent"
            @click="doCloseEvent"
          >
            <font-awesome-icon icon="check-circle" class="me-1" />
            {{ closingEvent ? 'Closing…' : 'Close & Award Event' }}
          </button>
          <button class="btn-outline" @click="$router.push(`/procurement/sourcing-events/${$route.params.id}`)">
            <font-awesome-icon icon="external-link-alt" class="me-1" /> View Event
          </button>
        </div>
      </div>

      <!-- Summary chips -->
      <div class="summary-strip">
        <div class="summary-chip">
          <span class="chip-label">Vendors</span>
          <span class="chip-val">{{ data.summary.vendor_count }}</span>
        </div>
        <div class="summary-chip">
          <span class="chip-label">Line Items</span>
          <span class="chip-val">{{ data.summary.line_count }}</span>
        </div>
        <div class="summary-chip">
          <span class="chip-label">Lines Ordered</span>
          <span class="chip-val chip-val--green">{{ awardedCount }} / {{ data.summary.line_count }}</span>
        </div>
        <div class="summary-chip">
          <span class="chip-label">Lowest Bid (total)</span>
          <span class="chip-val chip-val--blue">{{ formatCurrency(data.summary.price_range_min, data.summary.currency) }}</span>
        </div>
        <div class="summary-chip">
          <span class="chip-label">Highest Bid (total)</span>
          <span class="chip-val chip-val--muted">{{ formatCurrency(data.summary.price_range_max, data.summary.currency) }}</span>
        </div>
      </div>

      <!-- All awarded banner -->
      <div v-if="data.all_lines_awarded && data.event.status !== 'awarded'" class="all-awarded-banner">
        <font-awesome-icon icon="check-circle" /> All line items have been assigned to purchase orders. You can now close and award the event.
      </div>

      <!-- Product grid -->
      <div class="section-header-row">
        <div class="section-title-main">Product Comparison</div>
        <div class="grid-toolbar">
          <button class="btn-sel-all" @click="selectAllPending" :disabled="pendingLines.length === 0">
            Select All Pending
          </button>
          <button class="btn-sel-clear" @click="selectedLineIds = []" :disabled="selectedLineIds.length === 0">
            Clear
          </button>
        </div>
      </div>

      <div class="product-grid">
        <div
          v-for="line in data.line_matrix"
          :key="line.line_id"
          :class="['product-row', line.po_line_id ? 'product-row--awarded' : '', selectedLineIds.includes(line.line_id) ? 'product-row--selected' : '']"
        >
          <!-- Checkbox + info col -->
          <div class="pr-left">
            <label class="pr-checkbox" :title="line.po_line_id ? 'Already on a PO' : 'Select for PO'">
              <input
                type="checkbox"
                :checked="selectedLineIds.includes(line.line_id)"
                :disabled="!!line.po_line_id"
                @change="toggleLine(line.line_id)"
              />
            </label>
            <div class="pr-info">
              <div class="pr-desc">{{ line.description }}</div>
              <div class="pr-meta">
                <span v-if="line.category" class="pr-cat">{{ line.category }}</span>
                <span class="pr-qty">{{ line.quantity }} {{ line.uom || '' }}</span>
                <span v-if="line.target_cost" class="pr-target">Target: {{ formatCurrency(line.target_cost, data.event.currency) }}</span>
              </div>
              <span v-if="line.po_line_id" class="badge-po-done">PO Created</span>
            </div>
          </div>

          <!-- Vendor bids -->
          <div class="pr-bids">
            <div
              v-for="cell in quotedCells(line)"
              :key="cell.vendor_id"
              :class="['bid-chip', cell.is_cheapest ? 'bid-chip--cheapest' : '']"
            >
              <div class="bid-vendor">{{ vendorName(cell.vendor_id) }}</div>
              <div class="bid-price">{{ formatCurrency(cell.unit_price, data.event.currency) }}</div>
              <div class="bid-sub">
                <span v-if="cell.lead_time" class="bid-lead">{{ cell.lead_time }}d</span>
                <span v-if="cell.is_cheapest" class="bid-cheapest-tag">Lowest</span>
              </div>
            </div>
            <div v-if="quotedCells(line).length === 0" class="no-bids">No bids yet</div>
          </div>
        </div>
      </div>

      <!-- PO creation panel -->
      <div v-if="selectedLineIds.length > 0" class="po-panel">
        <div class="po-panel-header">
          <div class="po-panel-title">
            Create Purchase Order
            <span class="po-panel-count">{{ selectedLineIds.length }} item{{ selectedLineIds.length > 1 ? 's' : '' }} selected</span>
          </div>
          <button class="btn-close-panel" @click="selectedLineIds = []">&times;</button>
        </div>
        <div class="po-panel-body">
          <div class="po-selected-lines">
            <div v-for="lid in selectedLineIds" :key="lid" class="po-sel-line">
              <font-awesome-icon icon="check" class="sel-check" />
              {{ lineDesc(lid) }}
            </div>
          </div>
          <div class="po-panel-field">
            <label class="field-label">Vendor <span class="req">*</span></label>
            <select v-model="panelVendorId" class="field-input">
              <option value="">— Select vendor —</option>
              <option v-for="v in eligibleVendors" :key="v.vendor_id" :value="v.vendor_id">
                {{ v.vendor_name }}
              </option>
            </select>
            <div v-if="eligibleVendors.length === 0" class="field-hint">No vendor has quoted all selected items.</div>
          </div>
          <button
            class="btn-open-po-modal"
            :disabled="!panelVendorId"
            @click="openPoModal"
          >
            <font-awesome-icon icon="file-alt" class="me-1" /> Configure &amp; Create PO
          </button>
        </div>
      </div>

      <!-- Vendor ranking (secondary / collapsible) -->
      <div class="vendor-ranking-section">
        <button class="ranking-toggle" @click="showRanking = !showRanking">
          <font-awesome-icon :icon="showRanking ? 'chevron-down' : 'chevron-right'" class="me-1" />
          Vendor Ranking &amp; Scores
        </button>
        <div v-if="showRanking" class="vendor-scorecards">
          <div
            v-for="(v, idx) in data.vendors"
            :key="v.vendor_id"
            :class="['scorecard', idx === 0 ? 'scorecard--top' : '']"
          >
            <div class="sc-rank">#{{ idx + 1 }}</div>
            <div class="sc-body">
              <div class="sc-name-row">
                <span class="sc-name">{{ v.vendor_name }}</span>
                <span v-if="idx === 0" class="badge-winner">Best</span>
                <span v-if="v.is_recommended" class="badge-rec">Recommended</span>
              </div>
              <div class="sc-num text-muted">{{ v.vendor_number }}</div>
              <div class="sc-score-row">
                <span class="sc-score-label">Overall Score</span>
                <span class="sc-score-val">{{ v.composite_score }}/100</span>
              </div>
              <div class="sc-bar-wrap">
                <div class="sc-bar" :style="{ width: v.composite_score + '%', background: scoreColor(v.composite_score) }"></div>
              </div>
              <div class="sc-metrics">
                <div class="metric-pill metric-pill--price">
                  <span class="mp-label">Total Quote</span>
                  <span class="mp-val">{{ formatCurrency(v.quoted_total, v.quoted_currency) }}</span>
                </div>
                <div class="metric-pill metric-pill--cover">
                  <span class="mp-label">Coverage</span>
                  <span class="mp-val">{{ v.coverage_pct }}%</span>
                </div>
                <div class="metric-pill metric-pill--lead">
                  <span class="mp-label">Lead Time</span>
                  <span class="mp-val">{{ v.avg_lead_time ? v.avg_lead_time + 'd' : '—' }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </template>

    <!-- Generate PO Modal -->
    <div v-if="showPoModal" class="modal-backdrop" @click.self="showPoModal = false">
      <div class="modal-box">
        <div class="modal-header">
          <h6>Generate Purchase Order</h6>
          <button class="btn-close-modal" @click="showPoModal = false">&times;</button>
        </div>
        <div class="modal-body">
          <div class="po-vendor-info" v-if="poVendorObj">
            <div class="po-vname">{{ poVendorObj.vendor_name }}</div>
            <div class="text-muted">{{ poVendorObj.vendor_number }}</div>
          </div>
          <div class="po-lines-summary">
            <div class="po-lines-label">Lines in this PO ({{ selectedLineIds.length }})</div>
            <div v-for="lid in selectedLineIds" :key="lid" class="po-line-item">{{ lineDesc(lid) }}</div>
          </div>
          <div class="field">
            <label class="field-label">PO Title <span class="req">*</span></label>
            <input v-model="poForm.title" class="field-input" placeholder="Purchase order title" />
          </div>
          <div class="field">
            <label class="field-label">Expected Delivery Date</label>
            <input v-model="poForm.expected_delivery_date" type="date" class="field-input" />
          </div>
          <div class="field">
            <label class="field-label">Delivery Address</label>
            <input v-model="poForm.delivery_address" class="field-input" :placeholder="data.event.delivery_address || 'Delivery address'" />
          </div>
          <div class="field">
            <label class="field-label">Payment Terms</label>
            <select v-model="poForm.payment_terms" class="field-input">
              <option value="">— No payment terms —</option>
              <option v-for="t in paymentTerms" :key="t.id" :value="t.name">{{ t.name }}</option>
            </select>
          </div>
          <div class="field">
            <label class="field-label">Notes</label>
            <textarea v-model="poForm.notes" class="field-input" rows="2" placeholder="Internal notes"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="showPoModal = false">Cancel</button>
          <button class="btn-primary" :disabled="!poForm.title || generatingPo" @click="doGeneratePo">
            {{ generatingPo ? 'Generating…' : 'Generate PO' }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';

export default {
  name: 'SourcingEventAnalysis',
  setup() { return { toast: useToast() }; },
  data() {
    return {
      loading: true,
      error: null,
      data: null,
      selectedLineIds: [],
      panelVendorId: '',
      showPoModal: false,
      poForm: { title: '', delivery_address: '', payment_terms: '', expected_delivery_date: '', notes: '' },
      generatingPo: false,
      closingEvent: false,
      paymentTerms: [],
      showRanking: false,
    };
  },
  computed: {
    awardedCount() {
      return (this.data?.line_matrix || []).filter(l => l.po_line_id).length;
    },
    pendingLines() {
      return (this.data?.line_matrix || []).filter(l => !l.po_line_id);
    },
    eligibleVendors() {
      if (!this.data || !this.selectedLineIds.length) return [];
      return this.data.vendors.filter(v =>
        this.selectedLineIds.every(lid => {
          const line = this.data.line_matrix.find(l => l.line_id === lid);
          if (!line) return false;
          const cell = line.cells.find(c => c.vendor_id === v.vendor_id);
          return cell && cell.quoted;
        })
      );
    },
    poVendorObj() {
      if (!this.panelVendorId || !this.data) return null;
      return this.data.vendors.find(v => v.vendor_id === this.panelVendorId) || null;
    },
  },
  async mounted() {
    const [, termsRes] = await Promise.allSettled([
      this.loadAnalysis(),
      axios.get('/procurement/payment-terms'),
    ]);
    if (termsRes.status === 'fulfilled') {
      this.paymentTerms = termsRes.value?.data?.data ?? [];
    }
  },
  methods: {
    async loadAnalysis() {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await axios.get(`/procurement/sourcing-events/${this.$route.params.id}/analysis`);
        this.data = data.data;
        this.selectedLineIds = [];
        this.panelVendorId = '';
      } catch (e) {
        this.error = e.response?.data?.message || 'Failed to load analysis';
      } finally {
        this.loading = false;
      }
    },
    vendorName(vendorId) {
      return this.data?.vendors.find(v => v.vendor_id === vendorId)?.vendor_name ?? vendorId;
    },
    quotedCells(line) {
      return (line.cells || []).filter(c => c.quoted);
    },
    lineDesc(lineId) {
      return this.data?.line_matrix.find(l => l.line_id === lineId)?.description ?? lineId;
    },
    toggleLine(lineId) {
      const idx = this.selectedLineIds.indexOf(lineId);
      if (idx === -1) this.selectedLineIds.push(lineId);
      else this.selectedLineIds.splice(idx, 1);
      this.panelVendorId = '';
    },
    selectAllPending() {
      this.selectedLineIds = this.pendingLines.map(l => l.line_id);
      this.panelVendorId = '';
    },
    openPoModal() {
      this.poForm = {
        title: this.data.event.title,
        delivery_address: this.data.event.delivery_address || '',
        payment_terms: this.data.event.payment_terms || '',
        expected_delivery_date: '',
        notes: '',
      };
      this.showPoModal = true;
    },
    async doGeneratePo() {
      if (!this.poForm.title.trim()) { this.toast.error('PO title is required'); return; }
      this.generatingPo = true;
      try {
        const { data } = await axios.post(`/procurement/sourcing-events/${this.$route.params.id}/generate-po`, {
          vendor_id: this.panelVendorId,
          line_ids: this.selectedLineIds,
          ...this.poForm,
        });
        this.toast.success('Purchase order created: ' + data.data.po_number);
        this.showPoModal = false;
        await this.loadAnalysis();
      } catch (e) {
        this.toast.error(e.response?.data?.message || 'Failed to generate PO');
      } finally {
        this.generatingPo = false;
      }
    },
    async doCloseEvent() {
      this.closingEvent = true;
      try {
        await axios.post(`/procurement/sourcing-events/${this.$route.params.id}/close`);
        this.toast.success('Sourcing event closed and awarded.');
        this.$router.push(`/procurement/sourcing-events/${this.$route.params.id}`);
      } catch (e) {
        this.toast.error(e.response?.data?.message || 'Failed to close event');
      } finally {
        this.closingEvent = false;
      }
    },
    scoreColor(score) {
      if (score >= 75) return '#22c55e';
      if (score >= 50) return '#60a5fa';
      if (score >= 30) return '#f59e0b';
      return '#f87171';
    },
    formatCurrency(amount, currency) {
      if (amount == null) return '—';
      const sym = { NGN: '₦', GBP: '£', USD: '$', EUR: '€' }[currency] ?? (currency ?? '');
      return sym + Number(amount).toLocaleString('en', { minimumFractionDigits: 2 });
    },
    formatStatus(s) {
      const map = {
        draft: 'Draft', open: 'Open', closed: 'Closed',
        evaluation: 'Evaluation', award_recommended: 'Award Recommended',
        awarded: 'Awarded', cancelled: 'Cancelled',
      };
      return map[s] ?? s;
    },
  },
};
</script>

<style scoped>
.analysis-page { padding: 0; max-width: 1200px; }

.back-btn { display:inline-flex; align-items:center; gap:6px; background:transparent; border:none; color:#7a90b0; font-size:13px; cursor:pointer; padding:0; margin-bottom:14px; }
.back-btn:hover { color:#004AAD; }
.me-1 { margin-right: 4px; }

/* Page header */
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:18px; }
.page-title  { font-size:22px; font-weight:700; color:#1a2b45; margin:0 0 4px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; display:flex; align-items:center; gap:6px; flex-wrap:wrap; }
.mono        { font-family:monospace; font-size:12px; color:#004AAD; font-weight:600; }
.header-actions { display:flex; gap:8px; align-items:center; }
.ev-status   { display:inline-block; font-size:11.5px; font-weight:600; border-radius:20px; padding:2px 10px; }
.ev-status--evaluation        { background:#e0f2fe; color:#0369a1; }
.ev-status--award_recommended { background:#fff7ed; color:#c2410c; }
.ev-status--awarded           { background:#d1fae5; color:#065f46; }
.ev-status--closed            { background:#fef9c3; color:#854d0e; }
.ev-status--open              { background:#dcfce7; color:#15803d; }

.btn-close-event { display:inline-flex; align-items:center; gap:6px; background:#065f46; color:#fff; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; }
.btn-close-event:hover:not(:disabled) { background:#047857; }
.btn-close-event:disabled { opacity:.5; cursor:not-allowed; }

/* Loading / error */
.loading-state { display:flex; flex-direction:column; align-items:center; padding:80px 24px; gap:12px; color:#7a90b0; }
.spinner { width:36px; height:36px; border:3px solid #e4eaf2; border-top-color:#004AAD; border-radius:50%; animation:spin .7s linear infinite; }
@keyframes spin { to { transform:rotate(360deg); } }
.error-state  { display:flex; flex-direction:column; align-items:center; padding:80px 24px; gap:14px; text-align:center; }
.error-icon   { font-size:40px; color:#d1d5db; }

/* Summary strip */
.summary-strip { display:flex; gap:10px; flex-wrap:wrap; margin-bottom:18px; }
.summary-chip  { background:#fff; border:1px solid #e4eaf2; border-radius:10px; padding:10px 18px; display:flex; flex-direction:column; gap:2px; }
.chip-label    { font-size:10.5px; font-weight:600; color:#7a90b0; text-transform:uppercase; letter-spacing:.05em; }
.chip-val      { font-size:18px; font-weight:700; color:#1a2b45; }
.chip-val--blue  { color:#004AAD; }
.chip-val--muted { color:#64748b; }
.chip-val--green { color:#15803d; }

/* All awarded banner */
.all-awarded-banner { background:#d1fae5; border:1px solid #6ee7b7; border-radius:10px; padding:12px 18px; color:#065f46; font-size:13px; font-weight:600; display:flex; align-items:center; gap:8px; margin-bottom:18px; }

/* Section header */
.section-header-row { display:flex; align-items:center; justify-content:space-between; margin-bottom:10px; }
.section-title-main { font-size:14px; font-weight:700; color:#1a2b45; }
.grid-toolbar { display:flex; gap:6px; }
.btn-sel-all, .btn-sel-clear { font-size:12px; font-weight:600; padding:6px 14px; border-radius:7px; border:1.5px solid #dde5f0; background:#fff; color:#374151; cursor:pointer; }
.btn-sel-all:hover:not(:disabled) { background:#eff6ff; border-color:#93c5fd; color:#004AAD; }
.btn-sel-clear:hover:not(:disabled) { background:#fef2f2; border-color:#fca5a5; color:#dc2626; }
.btn-sel-all:disabled, .btn-sel-clear:disabled { opacity:.4; cursor:not-allowed; }

/* Product grid */
.product-grid { display:flex; flex-direction:column; gap:0; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; margin-bottom:20px; }

.product-row { display:flex; gap:0; align-items:stretch; border-bottom:1px solid #f0f4f8; background:#fff; transition:background .1s; }
.product-row:last-child { border-bottom:none; }
.product-row--selected { background:#eff6ff; }
.product-row--awarded  { background:#f0fdf4; }

.pr-left { display:flex; align-items:flex-start; gap:10px; padding:14px 16px; min-width:240px; max-width:300px; flex-shrink:0; border-right:1px solid #f0f4f8; }
.pr-checkbox input[type="checkbox"] { width:16px; height:16px; margin-top:3px; cursor:pointer; accent-color:#004AAD; flex-shrink:0; }
.pr-checkbox input:disabled { cursor:not-allowed; accent-color:#9ca3af; }
.pr-info { display:flex; flex-direction:column; gap:4px; min-width:0; }
.pr-desc { font-size:13.5px; font-weight:600; color:#1a2b45; word-break:break-word; }
.pr-meta { display:flex; flex-wrap:wrap; gap:6px; }
.pr-cat    { font-size:11px; background:#f1f5f9; color:#475569; border-radius:5px; padding:1px 7px; }
.pr-qty    { font-size:11.5px; color:#7a90b0; }
.pr-target { font-size:11.5px; color:#b45309; }
.badge-po-done { display:inline-block; background:#d1fae5; color:#065f46; font-size:10.5px; font-weight:700; padding:2px 9px; border-radius:20px; margin-top:2px; }

.pr-bids { flex:1; display:flex; flex-wrap:wrap; align-items:center; gap:8px; padding:12px 16px; }
.bid-chip { display:flex; flex-direction:column; gap:2px; background:#f8fafc; border:1px solid #e4eaf2; border-radius:9px; padding:8px 12px; min-width:110px; }
.bid-chip--cheapest { background:#f0fdf4; border-color:#86efac; }
.bid-vendor { font-size:10.5px; font-weight:700; color:#7a90b0; text-transform:uppercase; letter-spacing:.04em; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:130px; }
.bid-chip--cheapest .bid-vendor { color:#15803d; }
.bid-price { font-size:14px; font-weight:700; color:#1a2b45; }
.bid-chip--cheapest .bid-price { color:#065f46; }
.bid-sub { display:flex; align-items:center; gap:5px; margin-top:1px; }
.bid-lead { font-size:10.5px; color:#7a90b0; }
.bid-cheapest-tag { font-size:9.5px; font-weight:700; background:#dcfce7; color:#15803d; padding:1px 6px; border-radius:4px; }
.no-bids { font-size:12px; color:#d1d5db; font-style:italic; }

/* PO panel */
.po-panel { background:#fff; border:2px solid #004AAD; border-radius:12px; margin-bottom:20px; overflow:hidden; }
.po-panel-header { display:flex; align-items:center; justify-content:space-between; background:#eff6ff; padding:12px 18px; border-bottom:1px solid #bfdbfe; }
.po-panel-title { font-size:14px; font-weight:700; color:#004AAD; display:flex; align-items:center; gap:10px; }
.po-panel-count { font-size:12px; font-weight:600; background:#004AAD; color:#fff; padding:2px 10px; border-radius:20px; }
.btn-close-panel { background:transparent; border:none; font-size:20px; color:#7a90b0; cursor:pointer; }
.po-panel-body { padding:16px 18px; display:flex; flex-direction:column; gap:12px; }
.po-selected-lines { display:flex; flex-direction:column; gap:4px; }
.po-sel-line { font-size:13px; color:#1a2b45; display:flex; align-items:center; gap:7px; }
.sel-check { color:#22c55e; font-size:11px; }
.po-panel-field { display:flex; flex-direction:column; gap:5px; }
.field-hint { font-size:11px; color:#f59e0b; margin-top:3px; }
.btn-open-po-modal { align-self:flex-start; display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 20px; border-radius:8px; border:none; cursor:pointer; }
.btn-open-po-modal:hover:not(:disabled) { background:#003b8e; }
.btn-open-po-modal:disabled { opacity:.4; cursor:not-allowed; }

/* Vendor ranking (secondary) */
.vendor-ranking-section { margin-top:4px; }
.ranking-toggle { background:transparent; border:none; font-size:13px; font-weight:700; color:#7a90b0; cursor:pointer; padding:6px 0; display:flex; align-items:center; gap:6px; margin-bottom:8px; }
.ranking-toggle:hover { color:#1a2b45; }
.vendor-scorecards { display:grid; grid-template-columns:repeat(auto-fill, minmax(280px, 1fr)); gap:12px; }
.scorecard { background:#fff; border:1.5px solid #e4eaf2; border-radius:12px; padding:14px 16px; display:flex; gap:12px; }
.scorecard--top { border-color:#22c55e; background:linear-gradient(135deg,#fff,#f0fdf4); }
.sc-rank { font-size:20px; font-weight:800; color:#e4eaf2; min-width:28px; padding-top:2px; }
.scorecard--top .sc-rank { color:#bbf7d0; }
.sc-body  { flex:1; min-width:0; }
.sc-name-row { display:flex; align-items:center; gap:6px; flex-wrap:wrap; margin-bottom:2px; }
.sc-name  { font-size:14px; font-weight:700; color:#1a2b45; }
.sc-num   { font-size:11.5px; margin-bottom:8px; }
.badge-winner { background:#dcfce7; color:#15803d; font-size:10.5px; font-weight:700; padding:2px 8px; border-radius:20px; }
.badge-rec    { background:#fef9c3; color:#854d0e; font-size:10.5px; font-weight:700; padding:2px 8px; border-radius:20px; }
.sc-score-row { display:flex; justify-content:space-between; align-items:center; margin-bottom:4px; }
.sc-score-label { font-size:11px; color:#7a90b0; }
.sc-score-val   { font-size:13px; font-weight:700; color:#1a2b45; }
.sc-bar-wrap { background:#f1f5f9; border-radius:6px; height:8px; margin-bottom:10px; overflow:hidden; }
.sc-bar { height:8px; border-radius:6px; transition:width .4s; }
.sc-metrics { display:flex; gap:6px; flex-wrap:wrap; }
.metric-pill { display:flex; flex-direction:column; background:#f8fafc; border:1px solid #e4eaf2; border-radius:7px; padding:5px 10px; min-width:72px; }
.mp-label { font-size:9.5px; font-weight:600; color:#7a90b0; text-transform:uppercase; }
.mp-val   { font-size:12.5px; font-weight:700; color:#1a2b45; }
.metric-pill--price { border-color:#bfdbfe; background:#eff6ff; }
.metric-pill--price .mp-val { color:#004AAD; }
.metric-pill--cover { border-color:#86efac; background:#f0fdf4; }
.metric-pill--cover .mp-val { color:#15803d; }
.metric-pill--lead  { border-color:#fde68a; background:#fefce8; }
.metric-pill--lead  .mp-val { color:#92400e; }

/* Modal */
.modal-backdrop { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1000; display:flex; align-items:center; justify-content:center; padding:20px; }
.modal-box   { background:#fff; border-radius:14px; width:100%; max-width:520px; box-shadow:0 20px 60px rgba(0,0,0,.2); max-height:90vh; overflow-y:auto; }
.modal-header { display:flex; align-items:center; justify-content:space-between; padding:18px 24px; border-bottom:1px solid #e4eaf2; position:sticky; top:0; background:#fff; z-index:1; }
.modal-header h6 { font-size:16px; font-weight:700; color:#1a2b45; margin:0; }
.btn-close-modal { background:transparent; border:none; font-size:22px; color:#7a90b0; cursor:pointer; }
.modal-body  { padding:20px 24px; display:flex; flex-direction:column; gap:12px; }
.modal-footer { display:flex; justify-content:flex-end; gap:10px; padding:16px 24px; border-top:1px solid #e4eaf2; position:sticky; bottom:0; background:#fff; z-index:1; }
.field { display:flex; flex-direction:column; gap:5px; }
.field-label { font-size:12.5px; font-weight:600; color:#374151; }
.req { color:#dc2626; }
.field-input { padding:9px 12px; border:1.5px solid #dde5f0; border-radius:8px; font-size:13px; outline:none; background:#fff; width:100%; box-sizing:border-box; resize:vertical; }
.field-input:focus { border-color:#004AAD; }
.po-vendor-info { background:#f8fafc; border:1px solid #e4eaf2; border-radius:8px; padding:12px 16px; }
.po-vname { font-weight:700; font-size:15px; color:#1a2b45; }
.po-lines-summary { background:#f0fdf4; border:1px solid #86efac; border-radius:8px; padding:10px 14px; }
.po-lines-label { font-size:11px; font-weight:700; color:#065f46; text-transform:uppercase; letter-spacing:.05em; margin-bottom:6px; }
.po-line-item { font-size:13px; color:#1a2b45; padding:3px 0; border-bottom:1px solid #d1fae5; }
.po-line-item:last-child { border-bottom:none; }
.btn-primary { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 20px; border-radius:8px; border:none; cursor:pointer; }
.btn-primary:hover:not(:disabled) { background:#003b8e; }
.btn-primary:disabled { opacity:.5; cursor:not-allowed; }
.btn-outline { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#1a2b45; border:1.5px solid #dde5f0; font-size:13px; font-weight:500; padding:9px 18px; border-radius:8px; cursor:pointer; }
.btn-outline:hover { background:#f4f7fb; }
.text-muted { color:#aab8cc; font-size:12px; }
</style>
